import type { OrderSummary } from '../../shared/orderStatus';
import { formatDate, relativeTime } from '../utils/time';

interface OrderResultsProps {
  orders: OrderSummary[];
  onSelect: (systemId: string) => void;
  onBack: () => void;
}

function statusStyle(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('complete') || s === 'delivered' || s.includes('closed'))
    return 'bg-brand-100 text-brand-700 border-brand-200';
  if (s.includes('ship')) return 'bg-blue-100 text-blue-700 border-blue-200';
  if (s.includes('progress') || s === 'mixed') return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

export function OrderResults({ orders, onSelect, onBack }: OrderResultsProps) {
  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 px-6 py-10 text-center">
        <h2 className="text-lg font-semibold mb-1">No orders found</h2>
        <p className="text-sm text-slate-500 mb-6">
          We couldn&rsquo;t find any orders matching what you entered. Double-check your email or
          order number and try again.
        </p>
        <button onClick={onBack} className="btn-pill-secondary">
          Back to lookup
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">
          {orders.length} order{orders.length === 1 ? '' : 's'} found
        </h2>
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-brand-600 cursor-pointer">
          ← New search
        </button>
      </div>

      {orders.map((o) => (
        <button
          key={o.systemId}
          onClick={() => onSelect(o.systemId)}
          className="
            w-full text-left bg-white rounded-xl border border-slate-200
            hover:border-brand-300 hover:shadow-md transition-all duration-200
            px-5 py-4 cursor-pointer
          "
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-bold text-slate-900">{o.cbmOrderNo}</span>
                {o.orderGlobalStatus && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusStyle(o.orderGlobalStatus)}`}
                  >
                    {o.orderGlobalStatus}
                  </span>
                )}
              </div>
              {o.description && (
                <p className="text-sm text-slate-600 truncate">{o.description}</p>
              )}
              {o.sellToCustomerName && (
                <p className="text-xs text-slate-400 mt-0.5">{o.sellToCustomerName}</p>
              )}
            </div>
            {o.systemCreatedAt && (
              <div className="text-right text-xs text-slate-400 shrink-0">
                <p title={formatDate(o.systemCreatedAt)}>{relativeTime(o.systemCreatedAt)}</p>
              </div>
            )}
          </div>
        </button>
      ))}
    </div>
  );
}
